import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Users, Search, Crown, Mail, Phone, Calendar, CreditCard,
  Loader2, Eye, Edit2, Trash2, Check, X, Gift, Camera,
  ChevronLeft, ChevronRight, Filter, UserCheck, UserX,
  TrendingUp, Sparkles, Trophy, Megaphone, DollarSign,
  ArrowUpDown, Flame, Zap, Star, ShoppingCart
} from 'lucide-react';
import { toast } from 'sonner';
import AdminLayout from '../../components/AdminLayout';
import { SidebarProvider } from '../../components/AdminSidebarNew';
import { publicUsersApi } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

const PublicUsersAdminPage = () => {
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [subscriptionOnly, setSubscriptionOnly] = useState(false);
  const [authProviderFilter, setAuthProviderFilter] = useState('all');
  const [sortBy, setSortBy] = useState('recent');
  const [segment, setSegment] = useState(null); // null | 'vip_buyers' | 'bravx_champions' | 'subscribers' | 'inactive'
  const [page, setPage] = useState(0);
  const limit = 20;

  // Modal states
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [editData, setEditData] = useState({});
  const [subscriptionMonths, setSubscriptionMonths] = useState(1);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    loadUsers();
  }, [page, subscriptionOnly, authProviderFilter, sortBy, segment]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const response = await publicUsersApi.adminList({
        search: search || undefined,
        subscription_only: subscriptionOnly || undefined,
        auth_provider: authProviderFilter !== 'all' ? authProviderFilter : undefined,
        sort_by: sortBy,
        segment: segment || undefined,
        limit,
        skip: page * limit
      });
      setUsers(response.data.users);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Error loading users:', error);
      toast.error('Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(0);
    loadUsers();
  };

  const openUserDetail = async (user) => {
    setSelectedUser(user);
    setShowDetailModal(true);
    try {
      const response = await publicUsersApi.adminGetUser(user.user_id);
      setUserDetails(response.data);
    } catch (error) {
      console.error('Error loading user details:', error);
      toast.error('Error al cargar detalles');
    }
  };

  const openEditModal = (user) => {
    setSelectedUser(user);
    setEditData({
      full_name: user.full_name || '',
      dni: user.dni || '',
      phone: user.phone || '',
      email_verified: user.email_verified || false,
      free_hd_downloads_remaining: user.free_hd_downloads_remaining || 0
    });
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    setActionLoading(true);
    try {
      await publicUsersApi.adminUpdateUser(selectedUser.user_id, editData);
      toast.success('Usuario actualizado');
      setShowEditModal(false);
      loadUsers();
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error('Error al actualizar');
    } finally {
      setActionLoading(false);
    }
  };

  const openSubscriptionModal = (user) => {
    setSelectedUser(user);
    setSubscriptionMonths(1);
    setShowSubscriptionModal(true);
  };

  const handleToggleSubscription = async (action) => {
    setActionLoading(true);
    try {
      await publicUsersApi.adminToggleSubscription(
        selectedUser.user_id, 
        action, 
        action === 'activate' ? subscriptionMonths : 1
      );
      toast.success(action === 'activate' ? 'Suscripción activada' : 'Suscripción desactivada');
      setShowSubscriptionModal(false);
      loadUsers();
    } catch (error) {
      console.error('Error toggling subscription:', error);
      toast.error('Error al modificar suscripción');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteUser = async (user) => {
    if (!confirm(`¿Estás seguro de eliminar a ${user.email}? Esta acción no se puede deshacer.`)) {
      return;
    }
    try {
      await publicUsersApi.adminDeleteUser(user.user_id);
      toast.success('Usuario eliminado');
      loadUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Error al eliminar');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('es-AR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <SidebarProvider>
      <AdminLayout>
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="p-6 lg:p-8 space-y-6"
        >
          {/* Hero header with aggregate stats */}
          <div className="relative bg-gradient-to-br from-zinc-900 via-zinc-900/80 to-transparent border border-zinc-800/60 rounded-2xl p-6 overflow-hidden" data-testid="users-hero">
            <div className="absolute top-0 right-0 w-64 h-64 bg-amber-500/5 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-emerald-500/5 rounded-full blur-3xl pointer-events-none" />
            <div className="relative flex flex-col sm:flex-row sm:items-start justify-between gap-6">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-xl bg-[#D4AF37]/15 border border-[#D4AF37]/30 flex items-center justify-center">
                    <Users className="text-[#D4AF37]" size={20} />
                  </div>
                  <div>
                    <h1 className="text-2xl lg:text-3xl font-black uppercase tracking-tight text-white">
                      Comunidad Bravo
                    </h1>
                    <p className="text-zinc-500 text-xs">Gestioná los usuarios, identificá VIPs y lanzá campañas con un click</p>
                  </div>
                </div>
              </div>

              {/* Aggregate stat cards */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 w-full sm:w-auto">
                <div className="bg-blue-500/5 border border-blue-500/30 rounded-xl px-4 py-3 min-w-[110px]">
                  <p className="text-[10px] uppercase tracking-wider text-blue-300">Total</p>
                  <p className="text-2xl font-black text-blue-400">{total.toLocaleString()}</p>
                </div>
                <button
                  onClick={() => { setSegment('vip_buyers'); setPage(0); }}
                  className={`bg-emerald-500/5 border ${segment === 'vip_buyers' ? 'border-emerald-500' : 'border-emerald-500/30'} hover:border-emerald-500/70 rounded-xl px-4 py-3 text-left transition-colors min-w-[110px]`}
                  data-testid="segment-vip"
                >
                  <p className="text-[10px] uppercase tracking-wider text-emerald-300 flex items-center gap-1">
                    <Trophy size={9} /> VIPs
                  </p>
                  <p className="text-2xl font-black text-emerald-400">
                    {users.filter(u => (u.total_spent || 0) >= 50000).length || '—'}
                  </p>
                  <p className="text-[9px] text-emerald-300/70 mt-0.5">≥ $50k gastado</p>
                </button>
                <button
                  onClick={() => { setSegment('bravx_champions'); setPage(0); }}
                  className={`bg-[#D4AF37]/5 border ${segment === 'bravx_champions' ? 'border-[#D4AF37]' : 'border-[#D4AF37]/30'} hover:border-[#D4AF37]/70 rounded-xl px-4 py-3 text-left transition-colors min-w-[110px]`}
                  data-testid="segment-champions"
                >
                  <p className="text-[10px] uppercase tracking-wider text-[#D4AF37] flex items-center gap-1">
                    <Sparkles size={9} /> BRAVX
                  </p>
                  <p className="text-2xl font-black text-[#D4AF37]">
                    {users.filter(u => (u.bravx_points || 0) >= 100).length || '—'}
                  </p>
                  <p className="text-[9px] text-[#D4AF37]/70 mt-0.5">≥ 100 pts</p>
                </button>
                <button
                  onClick={() => { setSegment('subscribers'); setPage(0); }}
                  className={`bg-purple-500/5 border ${segment === 'subscribers' ? 'border-purple-500' : 'border-purple-500/30'} hover:border-purple-500/70 rounded-xl px-4 py-3 text-left transition-colors min-w-[110px]`}
                  data-testid="segment-subscribers"
                >
                  <p className="text-[10px] uppercase tracking-wider text-purple-300 flex items-center gap-1">
                    <Crown size={9} /> FULL BRAVX
                  </p>
                  <p className="text-2xl font-black text-purple-400">
                    {users.filter(u => u.subscription_active).length || '—'}
                  </p>
                  <p className="text-[9px] text-purple-300/70 mt-0.5">Suscriptores</p>
                </button>
              </div>
            </div>

            {/* Quick segment chips + active filter indicator */}
            <div className="relative mt-5 flex items-center gap-2 flex-wrap" data-testid="segment-chips">
              <span className="text-[10px] uppercase tracking-wider text-zinc-500 mr-2">Segmentos rápidos:</span>
              {[
                { id: null,                label: 'Todos',          icon: Users,    color: 'zinc' },
                { id: 'vip_buyers',        label: 'VIPs',           icon: Trophy,   color: 'emerald' },
                { id: 'bravx_champions',   label: 'BRAVX 100+',     icon: Sparkles, color: 'amber' },
                { id: 'subscribers',       label: 'FULL BRAVX',     icon: Crown,    color: 'purple' },
                { id: 'inactive',          label: 'Inactivos',      icon: UserX,    color: 'red' },
              ].map(seg => {
                const Ic = seg.icon;
                const active = segment === seg.id;
                return (
                  <button
                    key={seg.id || 'all'}
                    onClick={() => { setSegment(seg.id); setPage(0); }}
                    data-testid={`chip-${seg.id || 'all'}`}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                      active
                        ? seg.color === 'emerald' ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/50' :
                          seg.color === 'amber'   ? 'bg-amber-500/20 text-amber-300 border border-amber-500/50' :
                          seg.color === 'purple'  ? 'bg-purple-500/20 text-purple-300 border border-purple-500/50' :
                          seg.color === 'red'     ? 'bg-red-500/20 text-red-300 border border-red-500/50' :
                                                    'bg-zinc-700 text-white border border-zinc-600'
                        : 'bg-zinc-800/50 text-zinc-400 hover:text-white border border-zinc-700'
                    }`}
                  >
                    <Ic size={11} />
                    {seg.label}
                  </button>
                );
              })}
              {/* Bulk campaign CTA */}
              {segment && (
                <button
                  onClick={() => {
                    // Send to all matching segment (use existing AdminCampaignComposePage)
                    const audMap = { vip_buyers: 'all_buyers', subscribers: 'consented_users', bravx_champions: 'all_users', inactive: 'all_users' };
                    navigate(`/admin/campanas/nueva?audience=${audMap[segment] || 'all_users'}`);
                  }}
                  className="ml-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold bg-gradient-to-r from-[#D4AF37] to-[#F5D25A] hover:from-[#F5D25A] hover:to-[#D4AF37] text-black"
                  data-testid="segment-campaign-btn"
                >
                  <Megaphone size={11} />
                  Crear campaña para este segmento
                </button>
              )}
            </div>
          </div>

          {/* Filters + Sort */}
          <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-4">
            <form onSubmit={handleSearch} className="flex flex-col md:flex-row items-stretch md:items-center gap-3">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={16} />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Buscar por email, nombre o DNI…"
                  className="pl-10 bg-zinc-950 border-zinc-800 rounded-md"
                  data-testid="users-search-input"
                />
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {/* Sort by */}
                <Select value={sortBy} onValueChange={(v) => { setSortBy(v); setPage(0); }}>
                  <SelectTrigger className="w-[185px] bg-zinc-950 border-zinc-800" data-testid="users-sort">
                    <ArrowUpDown size={14} className="text-zinc-500" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="recent">Más recientes</SelectItem>
                    <SelectItem value="oldest">Más antiguos</SelectItem>
                    <SelectItem value="top_spender">💰 Más gastaron</SelectItem>
                    <SelectItem value="top_buyer">🎟️ Más compraron</SelectItem>
                    <SelectItem value="top_bravx">✦ Top BRAVX</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={authProviderFilter} onValueChange={(v) => { setAuthProviderFilter(v); setPage(0); }}>
                  <SelectTrigger className="w-[110px] bg-zinc-950 border-zinc-800">
                    <SelectValue placeholder="Auth" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="email">Email</SelectItem>
                    <SelectItem value="google">Google</SelectItem>
                  </SelectContent>
                </Select>
                <Button type="submit" variant="outline" className="border-zinc-700 text-zinc-300">
                  <Filter size={14} className="mr-2" />
                  Buscar
                </Button>
              </div>
            </form>
          </div>

          {/* Users Table */}
          <div className="admin-card overflow-hidden p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="animate-spin text-[#D4AF37]" size={32} />
              </div>
            ) : users.length === 0 ? (
              <div className="text-center py-16">
                <Users size={48} className="mx-auto mb-4 text-[#52525B]" />
                <p className="text-[#52525B]">No se encontraron usuarios</p>
              </div>
            ) : (
              <>
              <table className="w-full">
                <thead className="bg-[#0A0A0A] border-b border-[#27272A]">
                  <tr>
                    <th className="text-left py-4 px-4 text-xs uppercase tracking-wider text-[#A1A1AA] font-medium">Usuario</th>
                    <th className="text-left py-4 px-4 text-xs uppercase tracking-wider text-[#A1A1AA] font-medium">DNI</th>
                    <th className="text-left py-4 px-4 text-xs uppercase tracking-wider text-[#A1A1AA] font-medium">Registro</th>
                    <th className="text-right py-4 px-4 text-xs uppercase tracking-wider text-[#A1A1AA] font-medium">Gastado</th>
                    <th className="text-right py-4 px-4 text-xs uppercase tracking-wider text-[#A1A1AA] font-medium">BRAVX pts</th>
                    <th className="text-center py-4 px-4 text-xs uppercase tracking-wider text-[#A1A1AA] font-medium">Suscripción</th>
                    <th className="text-right py-4 px-4 text-xs uppercase tracking-wider text-[#A1A1AA] font-medium">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#27272A]">
                  {users.map((user, idx) => {
                    const showRank = (sortBy === 'top_spender' || sortBy === 'top_buyer' || sortBy === 'top_bravx') && page === 0 && idx < 10;
                    const rank = (page * limit) + idx + 1;
                    return (
                    <motion.tr
                      key={user.user_id}
                      className="hover:bg-[#1A1A1A] transition-colors"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                    >
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-3">
                          {showRank && (
                            <span className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-black ${
                              rank === 1 ? 'bg-[#D4AF37] text-black' :
                              rank === 2 ? 'bg-zinc-300 text-black' :
                              rank === 3 ? 'bg-amber-700 text-white' :
                                           'bg-zinc-700 text-zinc-300'
                            }`} title={`Rank #${rank}`}>
                              #{rank}
                            </span>
                          )}
                          {user.profile_picture ? (
                            <img src={user.profile_picture} alt="" className="w-10 h-10 rounded-full object-cover" />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-[#27272A] flex items-center justify-center text-white font-bold">
                              {user.full_name?.charAt(0)?.toUpperCase() || user.email?.charAt(0)?.toUpperCase()}
                            </div>
                          )}
                          <div>
                            <p className="font-medium text-white flex items-center gap-2 flex-wrap">
                              {user.full_name || 'Sin nombre'}
                              {user.auth_provider === 'google' && (
                                <span className="text-xs bg-blue-500/20 text-blue-400 px-1.5 py-0.5 rounded">G</span>
                              )}
                              {(user.total_spent || 0) >= 50000 && (
                                <Trophy size={11} className="text-emerald-400" title="VIP: ≥ $50k gastado" />
                              )}
                              {user.marketing_opt_out && (
                                <span
                                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-red-500/15 text-red-400 text-[10px] font-bold uppercase tracking-wider border border-red-500/30"
                                  title={`Baja de campañas${user.marketing_opt_out_at ? ' · ' + new Date(user.marketing_opt_out_at).toLocaleDateString('es-AR') : ''}`}
                                  data-testid={`user-optout-${user.user_id || user.id}`}
                                >
                                  Baja
                                </span>
                              )}
                            </p>
                            <p className="text-sm text-[#A1A1AA]">{user.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-4 text-[#A1A1AA] font-mono">
                        {user.dni || '-'}
                      </td>
                      <td className="py-4 px-4 text-[#A1A1AA] text-sm">
                        {formatDate(user.created_at)}
                      </td>
                      <td className="py-4 px-4 text-right">
                        {Number(user.total_spent || 0) > 0 ? (
                          <div data-testid={`user-spent-${user.user_id || user.id}`}>
                            <p className="text-white font-mono font-bold">${Number(user.total_spent).toLocaleString()}</p>
                            <p className="text-[10px] text-[#A1A1AA]">{user.total_orders || 0} órden(es) · {user.total_tickets || 0} entradas</p>
                          </div>
                        ) : (
                          <span className="text-[#52525B] text-xs">—</span>
                        )}
                      </td>
                      <td className="py-4 px-4 text-right">
                        {Number(user.bravx_points || 0) > 0 ? (
                          <span
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#D4AF37]/10 text-[#D4AF37] text-xs font-mono font-bold border border-[#D4AF37]/30"
                            data-testid={`user-bravx-${user.user_id || user.id}`}
                            title="BRAVX Points acumulados por referidos y compras"
                          >
                            ✦ {Number(user.bravx_points).toLocaleString()}
                          </span>
                        ) : (
                          <span className="text-[#52525B] text-xs">0</span>
                        )}
                      </td>
                      <td className="py-4 px-4 text-center">
                        {user.subscription_active ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-yellow-500/20 text-yellow-500 text-xs font-medium">
                            <Crown size={12} />
                            BRAVX
                          </span>
                        ) : (
                          <span className="text-[#52525B] text-xs">-</span>
                        )}
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openUserDetail(user)}
                            className="text-[#A1A1AA] hover:text-white"
                          >
                            <Eye size={16} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditModal(user)}
                            className="text-[#A1A1AA] hover:text-white"
                          >
                            <Edit2 size={16} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openSubscriptionModal(user)}
                            className={user.subscription_active ? 'text-yellow-500 hover:text-yellow-400' : 'text-[#A1A1AA] hover:text-yellow-500'}
                          >
                            <Crown size={16} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/admin/campanas/nueva?audience=all_users&email=${encodeURIComponent(user.email)}`)}
                            className="text-[#A1A1AA] hover:text-[#D4AF37]"
                            title="Crear campaña pre-cargada para este usuario"
                            data-testid={`row-campaign-btn-${user.user_id || user.id}`}
                          >
                            <Megaphone size={16} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteUser(user)}
                            className="text-[#A1A1AA] hover:text-red-500"
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </td>
                    </motion.tr>
                  );})}
                </tbody>
              </table>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between p-4 border-t border-[#27272A]">
              <p className="text-[#A1A1AA] text-sm">
                Mostrando {page * limit + 1} - {Math.min((page + 1) * limit, total)} de {total}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0}
                >
                  <ChevronLeft size={16} />
                </Button>
                <span className="text-[#A1A1AA] text-sm px-3">
                  Página {page + 1} de {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1}
                >
                  <ChevronRight size={16} />
                </Button>
              </div>
            </div>
          )}
              </>
            )}
          </div>

      {/* User Details Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="bg-[#121212] border-[#27272A] text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users size={20} />
              Detalles del Usuario
            </DialogTitle>
          </DialogHeader>
          {userDetails ? (
            <div className="space-y-6">
              {/* Stats cards — quick financial + BRAVX overview */}
              {(() => {
                const orders = userDetails.orders || [];
                const totalSpent = orders.reduce((s, o) => s + (o.total_amount || 0), 0);
                const totalTickets = orders.reduce((s, o) => s + (o.quantity || 0), 0);
                const points = userDetails.user.bravx_points || 0;
                return (
                  <div className="grid grid-cols-3 gap-3" data-testid="user-stats-cards">
                    <div className="bg-gradient-to-br from-emerald-500/15 to-emerald-500/5 border border-emerald-500/30 rounded-xl p-3">
                      <p className="text-[10px] uppercase tracking-wider text-emerald-300">Gastado total</p>
                      <p className="text-2xl font-black text-emerald-400 mt-1">${Number(totalSpent).toLocaleString()}</p>
                      <p className="text-[10px] text-[#A1A1AA] mt-0.5">{orders.length} órden(es) · {totalTickets} entradas</p>
                    </div>
                    <div className="bg-gradient-to-br from-[#D4AF37]/15 to-[#D4AF37]/5 border border-[#D4AF37]/30 rounded-xl p-3">
                      <p className="text-[10px] uppercase tracking-wider text-[#D4AF37]">BRAVX Points</p>
                      <p className="text-2xl font-black text-[#D4AF37] mt-1">✦ {Number(points).toLocaleString()}</p>
                      <p className="text-[10px] text-[#A1A1AA] mt-0.5">Referidos + compras</p>
                    </div>
                    <div className="bg-gradient-to-br from-blue-500/15 to-blue-500/5 border border-blue-500/30 rounded-xl p-3">
                      <p className="text-[10px] uppercase tracking-wider text-blue-300">Última compra</p>
                      <p className="text-sm font-bold text-blue-400 mt-1 truncate">
                        {orders[0]?.created_at ? new Date(orders[0].created_at).toLocaleDateString('es-AR', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                      </p>
                      <p className="text-[10px] text-[#A1A1AA] mt-0.5 truncate">{orders[0]?.tier_name || (orders.length ? '' : 'Sin compras aún')}</p>
                    </div>
                  </div>
                );
              })()}

              {/* User Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-[#A1A1AA] text-xs">Email</Label>
                  <p className="font-medium">{userDetails.user.email}</p>
                </div>
                <div>
                  <Label className="text-[#A1A1AA] text-xs">Nombre</Label>
                  <p className="font-medium">{userDetails.user.full_name || '-'}</p>
                </div>
                <div>
                  <Label className="text-[#A1A1AA] text-xs">DNI</Label>
                  <p className="font-mono">{userDetails.user.dni || '-'}</p>
                </div>
                <div>
                  <Label className="text-[#A1A1AA] text-xs">Teléfono</Label>
                  <p>{userDetails.user.phone || '-'}</p>
                </div>
                <div>
                  <Label className="text-[#A1A1AA] text-xs">Fecha de nacimiento</Label>
                  <p>{userDetails.user.birth_date || '-'}</p>
                </div>
                <div>
                  <Label className="text-[#A1A1AA] text-xs">Proveedor</Label>
                  <p className="capitalize">{userDetails.user.auth_provider || 'email'}</p>
                </div>
              </div>

              {/* Subscription */}
              <div className="bg-[#0A0A0A] border border-[#27272A] p-4 rounded">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-[#A1A1AA]">Suscripción</Label>
                  {userDetails.user.subscription_active ? (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-yellow-500/20 text-yellow-500 text-sm">
                      <Crown size={14} />
                      FULL BRAVX Activo
                    </span>
                  ) : (
                    <span className="text-[#52525B] text-sm">Sin suscripción</span>
                  )}
                </div>
                {userDetails.user.subscription_active && (
                  <div className="grid grid-cols-2 gap-4 mt-3 text-sm">
                    <div>
                      <span className="text-[#A1A1AA]">Expira:</span>{' '}
                      {formatDate(userDetails.user.subscription_expires)}
                    </div>
                    <div>
                      <span className="text-[#A1A1AA]">Fotos HD restantes:</span>{' '}
                      {userDetails.user.free_hd_downloads_remaining || 0}
                    </div>
                  </div>
                )}
              </div>

              {/* Tickets */}
              {userDetails.tickets?.length > 0 && (
                <div>
                  <Label className="text-[#A1A1AA] mb-2 block">Tickets ({userDetails.tickets.length})</Label>
                  <div className="max-h-32 overflow-y-auto space-y-2">
                    {userDetails.tickets.map((ticket, i) => (
                      <div key={i} className="bg-[#0A0A0A] border border-[#27272A] p-2 rounded text-sm flex justify-between">
                        <span>{ticket.event_title}</span>
                        <span className={`text-xs ${ticket.status === 'valid' ? 'text-green-500' : 'text-[#52525B]'}`}>
                          {ticket.status}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="animate-spin" size={24} />
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit User Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="bg-[#121212] border-[#27272A] text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit2 size={20} />
              Editar Usuario
            </DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">
              {selectedUser?.email}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Nombre completo</Label>
              <Input
                value={editData.full_name || ''}
                onChange={(e) => setEditData({ ...editData, full_name: e.target.value })}
                className="bg-[#0A0A0A] border-[#27272A]"
              />
            </div>
            <div className="space-y-2">
              <Label>DNI</Label>
              <Input
                value={editData.dni || ''}
                onChange={(e) => setEditData({ ...editData, dni: e.target.value })}
                className="bg-[#0A0A0A] border-[#27272A]"
              />
            </div>
            <div className="space-y-2">
              <Label>Teléfono</Label>
              <Input
                value={editData.phone || ''}
                onChange={(e) => setEditData({ ...editData, phone: e.target.value })}
                className="bg-[#0A0A0A] border-[#27272A]"
              />
            </div>
            <div className="space-y-2">
              <Label>Fotos HD gratis restantes</Label>
              <Input
                type="number"
                value={editData.free_hd_downloads_remaining || 0}
                onChange={(e) => setEditData({ ...editData, free_hd_downloads_remaining: parseInt(e.target.value) })}
                className="bg-[#0A0A0A] border-[#27272A] w-32"
              />
            </div>
            <div className="flex items-center gap-3">
              <Switch
                checked={editData.email_verified}
                onCheckedChange={(checked) => setEditData({ ...editData, email_verified: checked })}
              />
              <Label>Email verificado</Label>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowEditModal(false)}>
                Cancelar
              </Button>
              <Button onClick={handleSaveEdit} disabled={actionLoading}>
                {actionLoading ? <Loader2 className="animate-spin" size={16} /> : 'Guardar'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Subscription Modal */}
      <Dialog open={showSubscriptionModal} onOpenChange={setShowSubscriptionModal}>
        <DialogContent className="bg-[#121212] border-[#27272A] text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Crown className="text-yellow-500" size={20} />
              Gestionar Suscripción
            </DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">
              {selectedUser?.email}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6">
            {selectedUser?.subscription_active ? (
              <div className="text-center py-4">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded bg-yellow-500/20 text-yellow-500 mb-4">
                  <Crown size={18} />
                  <span className="font-medium">FULL BRAVX Activo</span>
                </div>
                <p className="text-[#A1A1AA] text-sm mb-6">
                  {selectedUser?.subscription_unlimited 
                    ? '♾️ Suscripción Indeterminada' 
                    : `Expira: ${formatDate(selectedUser?.subscription_expires)}`
                  }
                </p>
                <Button
                  variant="destructive"
                  onClick={() => handleToggleSubscription('deactivate')}
                  disabled={actionLoading}
                  className="w-full"
                >
                  {actionLoading ? <Loader2 className="animate-spin mr-2" size={16} /> : <UserX className="mr-2" size={16} />}
                  Desactivar suscripción
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-[#A1A1AA] text-sm">
                  Activa manualmente la suscripción FULL BRAVX para este usuario.
                </p>
                <div className="space-y-2">
                  <Label>Duración</Label>
                  <Select value={String(subscriptionMonths)} onValueChange={(v) => setSubscriptionMonths(v === 'unlimited' ? 'unlimited' : parseInt(v))}>
                    <SelectTrigger className="bg-[#0A0A0A] border-[#27272A]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#121212] border-[#27272A]">
                      <SelectItem value="1">1 mes</SelectItem>
                      <SelectItem value="3">3 meses</SelectItem>
                      <SelectItem value="6">6 meses</SelectItem>
                      <SelectItem value="12">12 meses</SelectItem>
                      <SelectItem value="unlimited">Indeterminado</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  onClick={() => handleToggleSubscription('activate')}
                  disabled={actionLoading}
                  className="w-full admin-btn-primary"
                >
                  {actionLoading ? <Loader2 className="animate-spin mr-2" size={16} /> : <UserCheck className="mr-2" size={16} />}
                  Activar suscripción {subscriptionMonths === 'unlimited' ? '(Indeterminado)' : `(${subscriptionMonths} ${subscriptionMonths === 1 ? 'mes' : 'meses'})`}
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
      </motion.div>
      </AdminLayout>
    </SidebarProvider>
  );
};

export default PublicUsersAdminPage;
